import {
  ApplicationConfig,
  ErrorHandler,
  provideAppInitializer,
  inject,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { Router, RouteReuseStrategy, provideRouter } from '@angular/router';
import { InitializerService } from './initializer.service';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { LanguageHeaderInterceptor } from './profile/language-header.interceptor';
import { RefreshTokenInterceptor } from '@app/refresh-token.interceptor';
import { WaterfordHeadersInterceptor } from '@app/waterford-headers.interceptor';
import { AppRouteReuseStrategy } from '@app/app-route-reuse-strategy';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideState, provideStore } from '@ngrx/store';
import { reducers } from './shared/app.reducers';
import { provideEffects } from '@ngrx/effects';
import { GoalsEffects } from './goals/state/goals.effects';
import { reducer as goalsReducer } from './goals/state/goals.reducer';
import { reducer as filteringReducer } from './filtering/state/filtering.reducer';
import { FilteringEffects } from './filtering/state/filtering.effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { reducer as authenticationReducer } from './authentication/state/authentication.reducer';
import { AuthenticationEffects } from './authentication/state/authentication.effects';
import { reducer as settingsReducer } from './settings/state/settings.reducer';
import { reducer as configurationReducer } from './configuration/state/configuration.reducer';
import { ConfigurationEffects } from './configuration/state/configuration.effects';
import { reducer as messagesReducer } from './messages-and-notifications/state/messages-and-notifications.reducer';
import { MessagesAndNotificationsEffects } from './messages-and-notifications/state/messages-and-notifications.effects';
import { ProfileEffects } from './profile/state/profile.effects';
import { provideNgProgressOptions } from 'ngx-progressbar';
import { progressInterceptor } from 'ngx-progressbar/http';
import { NOTIFICATIONS_CONFIG } from '@wam/notifications';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import * as authenticationActions from '@app/authentication/state/authentication.actions';
import { routes } from '@app/app.routes';
import { reportsAppRoutes } from '@app/reports-app/reports-app.routes';
import { isReportsApp } from '@app/reports-app/reports-app.util';

function resetNgRxState(reducer) {
  return function (state, action) {
    return reducer(action === authenticationActions.clearNgRxStore ? undefined : state, action);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(isReportsApp() ? reportsAppRoutes : routes),
    provideStore(reducers, {
      metaReducers: [resetNgRxState],
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    provideEffects(
      GoalsEffects,
      ProfileEffects,
      AuthenticationEffects,
      ConfigurationEffects,
      MessagesAndNotificationsEffects,
      FilteringEffects,
    ),
    provideState('authentication', authenticationReducer),
    provideState('settings', settingsReducer),
    provideState('configuration', configurationReducer),
    provideState('messages-and-notifications', messagesReducer),
    provideState('goals', goalsReducer),
    provideState('filtering', filteringReducer),
    provideStoreDevtools({
      maxAge: 50,
      logOnly: !isDevMode(),
      connectInZone: true,
    }),
    provideNgProgressOptions({
      spinner: false,
    }),
    provideTranslateService({
      loader: provideTranslateHttpLoader(),
    }),
    {
      provide: NOTIFICATIONS_CONFIG,
      useValue: { timeout: 4000 },
    },
    { provide: 'window', useFactory: () => globalThis },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
      const initializerFn = (
        (service: InitializerService) => () =>
          service.load()
      )(inject(InitializerService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WaterfordHeadersInterceptor,
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    provideAnimations(),
    provideZoneChangeDetection(),
    provideHttpClient(withXhr(), withInterceptors([progressInterceptor]), withInterceptorsFromDi()),
  ],
};
